/**
 * #570 / #598 — playUri confirmation vs PLAYING-PENDING for slow-start renderers.
 * STOPPED during hi-res prefetch is not a confirmed failure when acceptance evidence exists.
 */
import { TransportCommandError } from '../lib/connector';
import { diagnosticLog } from '../utils/diagnosticLogger'; import { settleFormatAttempt } from './formatCeiling';
import { useMaestroStore } from '../store/maestroStore';
import { type GaplessPrefetchZoneHint } from './types';
import {
  computeStartConfirmWindowMs,
  lastMeasuredDispatchLatencyMs,
  noteMeasuredDispatchLatencyMs,
  refreshSessionConfirmWindowMs,
  resetStartConfirmLatencyForTests,
  resolveInitialConfirmWindowMs,
  skipConfirmedFailureBeaconPayload,
} from './playUriStartConfirmWindow';
import { probePlayUriConfirmation } from './playUriConfirmProbe';
import { dropDeferredForOtherTrack, resetDeferredUntilPlaying, settleDeferredUntilPlaying } from './playUriStartGates';

export { computeStartConfirmWindowMs, START_CONFIRM_WINDOW_CAP_MS, START_CONFIRM_WINDOW_LATENCY_MULT, START_CONFIRM_WINDOW_MIN_MS } from './playUriStartConfirmWindow';

export type PlayUriAcceptanceEvidence =
  | 'tier_ack'
  | 'soap_play_ok'
  | 'proxy_stream_request'
  | 'renderer_transitioning'
  | 'renderer_playing'
  | 'position_gt_zero';

export type PlayUriConfirmOutcome = 'success' | 'pending' | 'confirmed_failure';

export type PlayUriPendingSession = {
  trackId: string;
  rendererId: string;
  rendererName: string;
  dispatchedAt: number;
  ackAt: number | null;
  dispatchLatencyMs: number | null;
  evidence: Set<PlayUriAcceptanceEvidence>;
  sawTransitioningOrPlaying: boolean;
  confirmWindowMs: number;
  pollTimer: ReturnType<typeof setTimeout> | null;
};

const DISPATCH_LATENCY_BEACON_MS = 2_000;
const LOCAL_TIER_HEALTH_MS = 30_000;

let pending: PlayUriPendingSession | null = null;
let dispatchStartedAt: number | null = null;
let lastLocalTierSuccessAt = 0;

/** #598 G — survives pending-window expiry so Sonos track-end can still advance. */
let queueOwnershipAcceptanceLatch: { trackId: string; rendererId: string } | null = null;

function resolveRendererContext(): {
  renderer: { id: string; name?: string } | null;
  zone: GaplessPrefetchZoneHint | null;
} {
  const store = useMaestroStore.getState();
  const renderer = store.activeConnectorRenderer;
  const zone = (store.discovery?.activeZone as GaplessPrefetchZoneHint | undefined) ?? null;
  return { renderer, zone };
}

export function resetPlayUriConfirmationForTests(): void {
  if (pending?.pollTimer) clearTimeout(pending.pollTimer);
  pending = null;
  dispatchStartedAt = null;
  resetStartConfirmLatencyForTests();
  lastLocalTierSuccessAt = 0;
  queueOwnershipAcceptanceLatch = null;
  resetDeferredUntilPlaying();
}

function activeQueueTrackId(): string {
  const store = useMaestroStore.getState();
  if (store.queue.length === 0 || store.queueIndex < 0) return '';
  return store.currentTrack?.id != null ? String(store.currentTrack.id) : '';
}

export function latchQueueOwnershipOnAcceptance(trackId: string, rendererId: string): void {
  queueOwnershipAcceptanceLatch = {
    trackId: String(trackId),
    rendererId,
  };
  void import('./TransportState').then(({ TransportState }) => {
    TransportState.claimTransportOwner('queue');
  }).catch(() => {});
  diagnosticLog('info', 'playback', 'queue_ownership_latched', {
    trackId: queueOwnershipAcceptanceLatch.trackId,
    rendererId: queueOwnershipAcceptanceLatch.rendererId,
  });
}

export function isQueueOwnershipLatched(): boolean {
  return queueOwnershipAcceptanceLatch != null;
}

export function latchedQueueOwnershipTrackId(): string | null {
  return queueOwnershipAcceptanceLatch?.trackId ?? null;
}

export function clearQueueOwnershipAcceptanceLatch(reason: string): void {
  if (!queueOwnershipAcceptanceLatch) return;
  diagnosticLog('info', 'playback', 'queue_ownership_latch_cleared', {
    reason,
    trackId: queueOwnershipAcceptanceLatch.trackId,
    rendererId: queueOwnershipAcceptanceLatch.rendererId,
  });
  queueOwnershipAcceptanceLatch = null;
}

export function clearQueueOwnershipLatchForNewTrack(trackId: string | number | undefined): void {
  if (trackId == null || !queueOwnershipAcceptanceLatch) return;
  if (String(trackId) !== queueOwnershipAcceptanceLatch.trackId) {
    clearQueueOwnershipAcceptanceLatch('new_track_play');
  }
}

/** #598 G — false failure must not strip queue ownership while acceptance evidence holds. */
export function shouldRetainQueueTransportOwnership(): boolean {
  const trackId = activeQueueTrackId();
  if (!trackId) return false;

  if (pending?.trackId === trackId && hasPlayUriAcceptanceEvidence(pending)) {
    return true;
  }
  return queueOwnershipAcceptanceLatch?.trackId === trackId;
}

/** Remote-renderer track-end reclaim (Anthony Lombardo / Sonos fixture). */
export function shouldReclaimQueueOwnershipForTrackEnd(): boolean {
  return shouldRetainQueueTransportOwnership();
}

export function markLocalTransportTierSuccess(): void {
  lastLocalTierSuccessAt = Date.now();
}

export function isLocalTransportTierHealthy(): boolean {
  return lastLocalTierSuccessAt > 0 && Date.now() - lastLocalTierSuccessAt < LOCAL_TIER_HEALTH_MS;
}

export function markPlayUriDispatchStarted(): void {
  dispatchStartedAt = Date.now();
}

export function notePlayUriTransportTierAck(tier: number, command: string): void {
  if (command !== 'play_uri' || dispatchStartedAt == null) return;
  const latencyMs = Date.now() - dispatchStartedAt;
  if (latencyMs >= DISPATCH_LATENCY_BEACON_MS) {
    diagnosticLog('warn', 'transport', 'dispatch_latency_ms', {
      command,
      tier,
      dispatch_latency_ms: latencyMs,
    });
  } else {
    diagnosticLog('info', 'transport', 'dispatch_latency_ms', {
      command,
      tier,
      dispatch_latency_ms: latencyMs,
    });
  }
  notePlayUriAcceptanceEvidence('tier_ack', { tier, dispatch_latency_ms: latencyMs });
  noteMeasuredDispatchLatencyMs(latencyMs);
  if (pending && pending.ackAt == null) {
    pending.ackAt = Date.now();
    pending.dispatchLatencyMs = latencyMs;
    refreshSessionConfirmWindowMs(pending, resolveRendererContext().zone);
  }
}

export function beginPlayUriPending(
  trackId: string,
  rendererId: string,
  rendererName: string,
): PlayUriPendingSession {
  if (pending?.pollTimer) clearTimeout(pending.pollTimer);
  dropDeferredForOtherTrack(String(trackId));
  const { renderer, zone } = resolveRendererContext();
  const session: PlayUriPendingSession = {
    trackId: String(trackId),
    rendererId,
    rendererName,
    dispatchedAt: dispatchStartedAt ?? Date.now(),
    ackAt: null,
    dispatchLatencyMs: lastMeasuredDispatchLatencyMs(),
    evidence: new Set<PlayUriAcceptanceEvidence>(),
    sawTransitioningOrPlaying: false,
    confirmWindowMs: resolveInitialConfirmWindowMs(renderer, zone, lastMeasuredDispatchLatencyMs()),
    pollTimer: null,
  };
  pending = session;
  return session;
}

export function getPlayUriPendingSession(): PlayUriPendingSession | null {
  return pending;
}

export function isPlayUriPending(trackId?: string | null): boolean {
  if (!pending) return false;
  if (trackId != null && String(trackId) !== pending.trackId) return false;
  return true;
}

export function shouldBlockDuplicatePlayUri(
  trackId: string | number | undefined,
  intent: 'user' | 'queue-advance' | 'user_skip' | string,
): boolean {
  if (!pending || trackId == null) return false;
  if (intent === 'user' || intent === 'user_skip') return false;
  return String(trackId) === pending.trackId;
}

export function notePlayUriAcceptanceEvidence(
  evidence: PlayUriAcceptanceEvidence,
  extra?: Record<string, unknown>,
): void {
  if (!pending) return;
  const hadEvidence = pending.evidence.size > 0;
  pending.evidence.add(evidence);
  if (evidence === 'renderer_transitioning' || evidence === 'renderer_playing') {
    pending.sawTransitioningOrPlaying = true;
  }
  if (evidence === 'tier_ack' && typeof extra?.dispatch_latency_ms === 'number') {
    pending.dispatchLatencyMs = extra.dispatch_latency_ms;
    noteMeasuredDispatchLatencyMs(extra.dispatch_latency_ms);
    refreshSessionConfirmWindowMs(pending, resolveRendererContext().zone);
  }
  if (!hadEvidence) {
    latchQueueOwnershipOnAcceptance(pending.trackId, pending.rendererId);
  }
  diagnosticLog('info', 'playback', 'play_uri_acceptance_evidence', {
    evidence,
    trackId: pending.trackId,
    rendererId: pending.rendererId,
    elapsed_ms: Date.now() - pending.dispatchedAt,
    ...extra,
  });
}

export function hasPlayUriAcceptanceEvidence(session: PlayUriPendingSession | null = pending): boolean {
  if (!session) return false;
  return session.evidence.size > 0;
}

export function clearPlayUriPending(reason: string): void {
  settleFormatAttempt(reason); if (!pending) return;
  if (pending.pollTimer) clearTimeout(pending.pollTimer);
  diagnosticLog('info', 'playback', 'play_uri_pending_cleared', {
    reason,
    trackId: pending.trackId,
    rendererId: pending.rendererId,
    elapsed_ms: Date.now() - pending.dispatchedAt,
    evidence: Array.from(pending.evidence),
  });
  const settledTrackId = pending.trackId;
  pending = null;
  dispatchStartedAt = null;
  settleDeferredUntilPlaying(settledTrackId, reason);
}

export function logPlayUriFalseFailureAverted(
  rendererName: string,
  evidence: PlayUriAcceptanceEvidence[],
  elapsedMs: number,
): void {
  diagnosticLog('info', 'playback', 'play_uri_false_failure_averted', {
    renderer: rendererName,
    evidence,
    elapsed_ms: elapsedMs,
  });
}

export function logPlayUriConfirmedFailure(reason: string, rendererName: string): void {
  diagnosticLog('info', 'playback', 'play_uri_confirmed_failure', {
    reason,
    renderer: rendererName,
  });
}

export function logSkipConfirmedFailure(reason: string, rendererName: string): void {
  diagnosticLog('info', 'playback', 'skip_confirmed_failure', skipConfirmedFailureBeaconPayload(reason, rendererName, pending));
}

function isInvokeBudgetExceeded(err: unknown): boolean {
  const msg = String((err as Error)?.message || err || '').toLowerCase();
  return msg.includes('invoke budget exceeded');
}

function isDaemonCommandError(err: unknown): boolean {
  if (!(err instanceof TransportCommandError)) return false;
  const msg = String(err.message || '').toLowerCase();
  if (isInvokeBudgetExceeded(err)) return false;
  if (msg.includes('soap') && (msg.includes('fail') || msg.includes('rejected') || msg.includes('http 500'))) {
    return true;
  }
  if (msg.includes('setavtransporturi') && msg.includes('fail')) return true;
  if (msg.includes('play failed')) return true;
  return false;
}

export async function evaluatePlayUriTransportError(
  err: unknown,
  track: { id?: string | number } | null | undefined,
  rendererId: string | undefined,
  rendererName: string,
  getTransportInfo: (id: string) => Promise<string>,
): Promise<PlayUriConfirmOutcome> {
  const trackId = track?.id != null ? String(track.id) : '';
  if (!trackId || !rendererId) return 'confirmed_failure';

  if (isDaemonCommandError(err)) {
    logPlayUriConfirmedFailure(String((err as Error).message || err), rendererName);
    clearQueueOwnershipAcceptanceLatch('daemon_command_error');
    clearPlayUriPending('daemon_command_error');
    return 'confirmed_failure';
  }

  const budgetExceeded = isInvokeBudgetExceeded(err);
  const tierErr = err instanceof TransportCommandError ? err.tier : null;

  const session = pending?.trackId === trackId
    ? pending
    : beginPlayUriPending(trackId, rendererId, rendererName);

  if (budgetExceeded || (tierErr != null && tierErr >= 3)) {
    notePlayUriAcceptanceEvidence('tier_ack', { tier: tierErr, budget_exceeded: budgetExceeded });
  }

  if (hasPlayUriAcceptanceEvidence(session)) {
    const outcome = await probePlayUriConfirmation(
      getTransportInfo,
      session,
      true,
      (evidence) => notePlayUriAcceptanceEvidence(evidence),
    );
    if (outcome === 'success' || outcome === 'pending') {
      logPlayUriFalseFailureAverted(
        rendererName,
        Array.from(session.evidence),
        Date.now() - session.dispatchedAt,
      );
      return outcome;
    }
    logPlayUriConfirmedFailure('confirmation_probe_stopped', rendererName);
    clearQueueOwnershipAcceptanceLatch('confirmation_probe_stopped');
    clearPlayUriPending('confirmation_probe_stopped');
    return 'confirmed_failure';
  }

  if (budgetExceeded || tierErr != null) {
    const outcome = await probePlayUriConfirmation(
      getTransportInfo,
      session,
      hasPlayUriAcceptanceEvidence(session),
      (evidence) => notePlayUriAcceptanceEvidence(evidence),
    );
    if (outcome === 'success' || outcome === 'pending') {
      if (hasPlayUriAcceptanceEvidence(session) || budgetExceeded) {
        logPlayUriFalseFailureAverted(
          rendererName,
          Array.from(session.evidence),
          Date.now() - session.dispatchedAt,
        );
      }
      return outcome;
    }
  }

  logPlayUriConfirmedFailure(String((err as Error)?.message || err || 'play_uri_failed'), rendererName);
  clearQueueOwnershipAcceptanceLatch('transport_error');
  clearPlayUriPending('transport_error');
  return 'confirmed_failure';
}

export function schedulePlayUriPendingPoll(
  getTransportInfo: (id: string) => Promise<string>,
  onResolved: (outcome: 'success' | 'confirmed_failure') => void,
): void {
  if (!pending) return;
  if (pending.pollTimer) clearTimeout(pending.pollTimer);
  const session = pending;
  pending.pollTimer = setTimeout(async () => {
    if (!pending || pending.trackId !== session.trackId) return;
    const outcome = await probePlayUriConfirmation(
      getTransportInfo,
      session,
      hasPlayUriAcceptanceEvidence(session),
      (evidence) => notePlayUriAcceptanceEvidence(evidence),
    );
    if (outcome === 'success') {
      clearPlayUriPending('poll_playing');
      onResolved('success');
      return;
    }
    if (outcome === 'confirmed_failure') {
      logPlayUriConfirmedFailure('pending_window_expired', session.rendererName);
      // #598 G — keep queue ownership latch when acceptance evidence exists; renderer may still be playing.
      if (!hasPlayUriAcceptanceEvidence(session)) {
        clearQueueOwnershipAcceptanceLatch('pending_window_expired');
      }
      clearPlayUriPending('pending_window_expired');
      onResolved('confirmed_failure');
      return;
    }
    schedulePlayUriPendingPoll(getTransportInfo, onResolved);
  }, 2000);
}

export function noteRendererTransportState(state: string): void {
  if (!pending) return;
  const upper = String(state || '').toUpperCase();
  if (upper.includes('PLAYING')) notePlayUriAcceptanceEvidence('renderer_playing');
  else if (upper.includes('TRANSITIONING')) notePlayUriAcceptanceEvidence('renderer_transitioning');
}

export function noteRendererPositionProgress(position: number): void {
  if (!pending || !(position > 0)) return;
  notePlayUriAcceptanceEvidence('position_gt_zero', { position });
}
